import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useNotifications } from '../../shared/hooks/useNotifications';
import { BellIcon } from '../../components/icons';
import './NotificationInbox.css';

export function NotificationInbox() {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    clearAll 
  } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (id: string, link?: string) => {
    markAsRead(id);
    setIsOpen(false);
    if (link) {
      navigate(link);
    }
  };

  return (
    <div className="notif-container" ref={dropdownRef}>
      <button
        type="button"
        className={`notif-bell-btn ${isOpen ? 'active' : ''}`}
        onClick={handleToggle}
        aria-label="Notifications"
        title="Notifications"
      >
        <BellIcon size={17} />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <div className="notif-title-group">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <span className="notif-unread-count">{unreadCount} new</span>
              )}
            </div>
            {notifications.length > 0 && (
              <button 
                type="button" 
                className="notif-mark-all"
                onClick={markAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <div className="notif-empty-icon" aria-hidden="true">🔔</div>
                <p>You're all caught up!</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notif-item ${!notif.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif.id, notif.link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(notif.id, notif.link);
                    }
                  }}
                >
                  <div className="notif-item-icon" aria-hidden="true">
                    {notif.icon || '🔔'}
                  </div>
                  <div className="notif-item-content">
                    <h4 className="notif-item-title">{notif.title}</h4>
                    <p className="notif-item-message">{notif.message}</p>
                    <span className="notif-item-time">{notif.time}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="notif-footer">
            <Link 
              to="/notifications" 
              className="notif-view-all"
              onClick={() => setIsOpen(false)}
            >
              View all updates
            </Link>
            {notifications.length > 0 && (
              <button 
                type="button" 
                className="notif-clear-all"
                onClick={clearAll}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
